/**
 * Sitzungs-Lebenszyklus: Wiederherstellen über das Refresh-Cookie, Anmelden, Abmelden, Gateway-Start.
 * Fällt das READY-Ereignis aus (Gateway nicht erreichbar), wird der Zustand per REST geladen.
 */
import type { AuthResponseDto, NotificationSettingDto, ReadyPayload, ServerFullDto } from '@lagerfeuer/shared';
import { create } from 'zustand';
import { applyAuthResponse, cancelScheduledRefresh, lastRefreshOutcome, refreshAccessToken, refreshRetryDelay } from '../../lib/api';
import { authApi, dmsApi, notificationsApi, readStateApi, serversApi, usersApi } from '../../lib/endpoints';
import { queryClient } from '../../lib/queryClient';
import { connectGateway, disconnectGateway } from '../../realtime/gateway';
import { appState, useAppStore } from '../../store/app';
import { useAuthStore } from '../../store/auth';

export type SessionStatus = 'unknown' | 'checking' | 'authenticated' | 'anonymous';

export const useSession = create<{
  status: SessionStatus;
  /** Server beim Wiederherstellen nicht erreichbar (5xx/429/Netz) – erneuter Versuch geplant. */
  unreachable: boolean;
  set(s: SessionStatus): void;
}>((set) => ({
  status: 'unknown',
  unreachable: false,
  set: (status) => set({ status }),
}));

let restoreAttempts = 0;
let restoreTimer: ReturnType<typeof setTimeout> | null = null;

let readyFallback: ReturnType<typeof setTimeout> | null = null;
const READY_TIMEOUT_MS = 8000;

function startRealtime(): void {
  connectGateway();
  if (readyFallback) clearTimeout(readyFallback);
  readyFallback = setTimeout(() => {
    if (!appState().ready && useAuthStore.getState().accessToken) void loadViaRest();
  }, READY_TIMEOUT_MS);
}

/** Ersatz für READY über REST (Gateway nicht erreichbar oder langsam). */
export async function loadViaRest(): Promise<void> {
  const [user, settings, servers, dms, readStates, notificationSettings] = await Promise.all([
    usersApi.me(),
    usersApi.settings(),
    serversApi.list(),
    dmsApi.list().catch(() => []),
    readStateApi.list().catch(() => []),
    notificationsApi.settings().catch((): NotificationSettingDto[] => []),
  ]);
  const full = (await Promise.all(servers.map((s) => serversApi.get(s.id).catch(() => null)))).filter((s): s is ServerFullDto => !!s);
  if (appState().ready) return; // READY kam zwischenzeitlich doch
  const payload: ReadyPayload = {
    user,
    settings,
    servers: full,
    dms,
    readStates,
    presences: [],
    voiceStates: [],
    notificationSettings,
    unreadNotifications: 0,
    limits: appState().limits,
    sessionId: 'rest',
  };
  appState().dispatch('READY', payload);
}

/** Beim App-Start: Sitzung über das httpOnly-Cookie wiederherstellen. */
export async function restoreSession(): Promise<boolean> {
  const st = useSession.getState();
  if (st.status === 'checking') return false;
  if (useAuthStore.getState().accessToken) {
    st.set('authenticated');
    startRealtime();
    return true;
  }
  st.set('checking');
  return attemptRestore();
}

/**
 * Ein Wiederherstellungsversuch. Nur 401/403 führen zur Anmeldung; ist der Server vorübergehend nicht
 * erreichbar (z. B. 502 während eines Deploys), bleibt es beim Ladebildschirm mit erneutem Versuch (Backoff).
 */
async function attemptRestore(): Promise<boolean> {
  restoreTimer = null;
  const ok = await refreshAccessToken();
  if (ok) {
    restoreAttempts = 0;
    useSession.setState({ unreachable: false });
    if (useSession.getState().status !== 'authenticated') {
      useSession.getState().set('authenticated');
      startRealtime();
    }
    return true;
  }
  if (lastRefreshOutcome() === 'transient' && useSession.getState().status === 'checking') {
    restoreAttempts++;
    useSession.setState({ unreachable: true });
    restoreTimer = setTimeout(() => void attemptRestore(), refreshRetryDelay(restoreAttempts));
    return false;
  }
  restoreAttempts = 0;
  useSession.setState({ unreachable: false });
  if (useSession.getState().status === 'checking') useSession.getState().set('anonymous');
  return false;
}

/** Laufende Wiederholung abbrechen (z. B. Anmeldung per Formular). */
function cancelRestoreRetry(): void {
  if (restoreTimer) clearTimeout(restoreTimer);
  restoreTimer = null;
  restoreAttempts = 0;
  useSession.setState({ unreachable: false });
}

export function completeLogin(data: AuthResponseDto): void {
  cancelRestoreRetry();
  applyAuthResponse(data);
  useSession.getState().set('authenticated');
  startRealtime();
}

/** Lokal abmelden (ohne Serveraufruf), z. B. nach INVALID_SESSION. */
export function clearLocalSession(): void {
  if (readyFallback) clearTimeout(readyFallback);
  cancelRestoreRetry();
  cancelScheduledRefresh();
  disconnectGateway();
  useAuthStore.getState().clear();
  appState().reset();
  queryClient.clear();
  useSession.getState().set('anonymous');
}

export async function logout(everywhere = false): Promise<void> {
  try {
    if (everywhere) await authApi.logoutAll();
    await authApi.logout();
  } catch {
    // Auch bei Fehlern lokal abmelden.
  }
  clearLocalSession();
}

// Token verloren (Refresh fehlgeschlagen, Konto gesperrt) → Sitzung beenden.
useAuthStore.subscribe((s, prev) => {
  if (prev.accessToken && !s.accessToken && useSession.getState().status === 'authenticated') clearLocalSession();
});
// Eigene Nutzerdaten aus READY/USER_UPDATE in den Auth-Store spiegeln.
useAppStore.subscribe((s, prev) => {
  if (s.me && s.me !== prev.me && useAuthStore.getState().accessToken) useAuthStore.setState({ user: s.me });
});
